/**
 * Body Region Normalization Utility
 *
 * Maps legacy body_region values from exam_family_registry to clean display groups.
 * The legacy values are inconsistent (e.g., C_SPINE, L_SPINE, ABDOMEN_PELVIS),
 * so this normalizes them for display in the template list sidebar.
 */

// Map body_region values to their display group
// Order matters: more specific patterns first (e.g., CHEST_ABD_PEL before CHEST)
const BODY_REGION_MAPPINGS: Array<{ group: string; patterns: string[] }> = [
  // Multi-region combinations (check first - more specific)
  { group: "Chest/Abdomen/Pelvis", patterns: ["CHEST_ABD_PEL", "CHEST_ABDOMEN_PELVIS"] },
  { group: "Chest/Abdomen", patterns: ["CHEST_ABD", "CHEST_ABDOMEN"] },
  { group: "Abdomen/Pelvis", patterns: ["ABDOMEN_PELVIS", "ABD_PELVIS"] },
  { group: "Head/C-Spine", patterns: ["HEAD_C_SPINE", "BRAIN_C_SPINE"] },
  { group: "Head/Neck", patterns: ["HEAD_AND_NECK", "HEAD_NECK"] },

  // Single regions - Head includes face, orbits, sinuses, TMJ, pituitary, mandible
  { group: "Head", patterns: [
    "HEAD", "HEAD_BRAIN", "BRAIN", "HEAD_FACE",
    "FACE", "FACIAL", "FACIAL_BONES",
    "ORBIT", "ORBITS",
    "SINUS", "SINUSES", "PARANASAL",
    "PITUITARY", "SELLA",
    "TMJ", "TEMPOROMANDIBULAR",
    "MANDIBLE", "MAXILLA", "MAXILLOFACIAL",
    "IAC", "TEMPORAL_BONE"
  ]},
  { group: "Neck", patterns: ["NECK", "THYROID"] },

  // Breast imaging
  { group: "Breast", patterns: [
    "BREAST",
    "MAMMOGRAPHY", "MAMMOGRAM", "MAMMO",
    "TOMOSYNTHESIS", "TOMO",
    "GALACTOGRAM", "DUCTOGRAM"
  ]},

  // Spine (separate levels)
  { group: "C-Spine", patterns: ["C_SPINE", "CERVICAL_SPINE"] },
  { group: "T-Spine", patterns: ["T_SPINE", "THORACIC_SPINE"] },
  { group: "L-Spine", patterns: ["L_SPINE", "LUMBAR_SPINE", "SACRUM"] },
  { group: "Whole Spine", patterns: ["CTL_SPINE", "CT_SPINE", "TL_SPINE", "SPINE"] },

  // Torso
  { group: "Chest", patterns: ["CHEST", "LUNGS"] },
  { group: "Cardiac", patterns: ["CARDIAC", "HEART", "CORONARY"] },
  { group: "Abdomen", patterns: ["ABDOMEN", "LIVER", "KIDNEY", "KIDNEYS", "PANCREAS", "SPLEEN", "ADRENAL", "RENAL", "MRCP"] },
  { group: "Pelvis", patterns: ["PELVIS", "PROSTATE", "UTERUS", "OVARY", "OVARIES", "BLADDER", "RECTUM"] },

  // Extremities
  { group: "Extremities", patterns: [
    "SHOULDER", "FOOT", "KNEE", "HIP", "ANKLE", "WRIST", "ELBOW", "HAND",
    "ARM", "LEG", "FOREARM", "UPPER_EXTREMITY", "LOWER_EXTREMITY",
    "FEMUR", "TIBIA", "FIBULA", "HUMERUS", "RADIUS", "ULNA",
    "FINGER", "TOE", "THUMB", "CLAVICLE", "SCAPULA"
  ]},

  // Whole body
  { group: "Whole Body", patterns: ["WHOLE_BODY", "TOTAL_BODY", "SKELETON"] },
];

// Modalities that default to "Whole Body" when no specific body_region
const WHOLE_BODY_MODALITIES = ["NM", "PET"];

/**
 * Normalizes a body_region value from the database to a clean display group.
 *
 * @param bodyRegion - Raw body_region value from exam_family_registry
 * @param modality - Modality to help disambiguate (e.g., NM/PET default to Whole Body)
 * @returns Normalized display group name
 */
export function normalizeBodyRegion(bodyRegion: string | null, modality?: string): string {
  const upper = bodyRegion?.toUpperCase() || "";

  // Check patterns in order (more specific first)
  for (const { group, patterns } of BODY_REGION_MAPPINGS) {
    if (patterns.some(p => upper === p || upper.startsWith(p + "_") || upper.endsWith("_" + p))) {
      return group;
    }
  }

  // If no match and modality is NM/PET, default to Whole Body
  if (modality && WHOLE_BODY_MODALITIES.includes(modality.toUpperCase())) {
    return "Whole Body";
  }

  return "Other";
}

/**
 * Canonical display order for body region groups.
 * Used for sorting the template list sidebar from head to toe.
 */
export const BODY_REGION_ORDER = [
  "Head",
  "Head/Neck",
  "Head/C-Spine",
  "Neck",
  "C-Spine",
  "T-Spine",
  "L-Spine",
  "Whole Spine",
  "Chest",
  "Breast",
  "Cardiac",
  "Abdomen",
  "Pelvis",
  "Abdomen/Pelvis",
  "Chest/Abdomen",
  "Chest/Abdomen/Pelvis",
  "Extremities",
  "Whole Body",
  "Other"
];
